import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast, { Toaster } from "react-hot-toast";
import { Pencil, Trash2, Plus, ChevronUp, ChevronDown, MessageSquare, CheckCircle, XCircle, Clock } from "lucide-react";
import AddWarningModal from "../../modals/Hrms/AddWarningModal";
import { fetchEmployees } from "../../redux/employeeSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchWarnings,
  addWarning,
  updateWarning,
  deleteWarning,
  fetchAllJustifications,
  updateJustificationStatus,
} from "../../redux/warningSlice";

const WarningDetails = () => {
  const { register, handleSubmit, reset, setValue } = useForm();
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentWarning, setCurrentWarning] = useState(null);
  const [expandedRowIndex, setExpandedRowIndex] = useState(null);

  // Get warnings and employees from Redux store
  const { warnings, justifications, status } = useSelector((state) => state.warnings || { warnings: [], justifications: [] });

  const { employees } = useSelector((state) => state?.employees || []);


  useEffect(() => {
    dispatch(fetchEmployees());
    dispatch(fetchWarnings());
    dispatch(fetchAllJustifications());
  }, [dispatch]);

  const onSubmit = async (data) => {
    if (isEdit && currentWarning) {
      await dispatch(updateWarning({ id: currentWarning.id, updatedData: data }));
      toast.success("Warning updated successfully!");
    } else {
      await dispatch(addWarning(data));
      toast.success("Warning added successfully!");
    }

    setIsModalOpen(false);
    reset();
  };

  const handleEdit = (id) => {
    const warningToEdit = warnings.find((warning) => warning.id === id);
    if (warningToEdit) {
      setCurrentWarning(warningToEdit);
      setIsEdit(true);
      setIsModalOpen(true);

      // Populate modal fields
      Object.keys(warningToEdit).forEach((key) => {
        setValue(key, warningToEdit[key]);
      });
    }
  };

  const handleDelete = async (id) => {
    await dispatch(deleteWarning(id));
    toast.success("Warning deleted successfully!");
  };

  const handleApproveJustification = async (justificationId) => {
    try {
      await dispatch(updateJustificationStatus({
        id: justificationId,
        status: "Approved"
      })).unwrap();
      toast.success("Justification approved!");
      dispatch(fetchAllJustifications()); // Refresh justifications
    } catch (error) {
      toast.error(error.message || "Failed to approve justification");
    }
  };

  const handleRejectJustification = async (justificationId) => {
    try {
      await dispatch(updateJustificationStatus({
        id: justificationId,
        status: "Rejected"
      })).unwrap();
      toast.success("Justification rejected!");
      dispatch(fetchAllJustifications()); // Refresh justifications
    } catch (error) {
      toast.error(error.message || "Failed to reject justification");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Approved":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "Rejected":
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Approved":
        return "bg-green-100 text-green-800";
      case "Rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-yellow-100 text-yellow-800";
    }
  };

  const toggleView = (index) => {
    setExpandedRowIndex(expandedRowIndex === index ? null : index);
  };

  const filteredWarnings = (warnings || []).filter((warning) => {
    const searchMatch = searchTerm
      ? `${warning.name}`.toLowerCase().includes(searchTerm.toLowerCase())
      : true;
    const locationMatch = locationFilter ? warning.location === locationFilter : true;
    return searchMatch && locationMatch;
  });
  

  return (
    <div className="container mx-auto p-4 border border-[#241763]">
      {/* Dashboard Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Cards Data */}
      </div>

      {/* Filters and Add Button */}
      <div className="mb-4 flex flex-col md:flex-row gap-4 items-center w-full">
        <input
          type="text"
          className="p-2 border border-gray-300 rounded w-full md:w-3/6"
          placeholder="Search by name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="p-2 border border-gray-300 rounded w-full md:w-2/6"
          value={locationFilter}
          onChange={(e) => setLocationFilter(e.target.value)}
        >
          <option value="">All Locations</option>
        </select>
        <button
          className="w-full md:w-1/6 flex items-center justify-center bg-[#241763] text-white p-2 rounded"
          onClick={() => {
            setIsEdit(false);
            setCurrentWarning(null);
            setIsModalOpen(true);
            reset();
          }}
        >
          <Plus className="h-4 w-4" />
          <span className="ml-2 text-sm">Add Warning</span>
        </button>
      </div>

      {/* Warning List Table */}
      <div className="overflow-x-auto">
        <table className="table-auto w-full mt-6">
          <thead className="py-4 border-b font-bold">
            <tr>
              <th>Emp. Name</th>
              <th>Location</th>
              <th>Warning Sub</th>
              <th>Warning Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody className="text-sm">
  {filteredWarnings.map((warning, index) => {
    const employee = employees.find((emp) => emp.email === warning.teacherEmail);
    return (
      <React.Fragment key={warning.id}>
        <tr className="hover:bg-gray-200">
          <td>{employee ? `${employee.firstName} ${employee.lastName}` : "N/A"}</td>
          <td>{employee ? employee.location : "N/A"}</td>
          <td>{warning.subject}</td>
          <td>{warning.warningDate}</td>
          <td>{warning.isActive ? "Active" : "Inactive"}</td>
          <td className="space-x-2 flex items-center py-2">
            <button
              className="bg-[#B6A5C9]/50 text-white p-2 rounded mr-2 hover:bg-[#241763]/60 group"
              onClick={() => handleEdit(warning.id)}
            >
              <Pencil className="text-[#241763] w-4 h-4 group-hover:text-white" />
            </button>
            <button
              className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
              onClick={() => handleDelete(warning.id)}
            >
              <Trash2 className="text-[#241763] w-4 h-4 group-hover:text-white" />
            </button>
            <div
              onClick={() => toggleView(index)}
              className="cursor-pointer hover:bg-[#241763]/60 group bg-[#B6A5C9]/50 p-2 rounded"
            >
              {expandedRowIndex === index ? (
                <ChevronUp size={16} className="group-hover:text-white" />
              ) : (
                <ChevronDown size={16} className="group-hover:text-white" />
              )}
            </div>
          </td>
        </tr>
        {expandedRowIndex === index && (
          <tr>
            <td colSpan="6">
              <div className="p-4 bg-gray-100">
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-800 mb-2">Warning Details:</h4>
                  <p className="text-gray-700">{warning.description}</p>
                </div>
                
                {/* Justifications Section */}
                <div className="mt-4">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Justifications:
                  </h4>
                  {justifications
                    .filter(j => j.warningId === warning.id)
                    .map((justification) => (
                      <div key={justification.id} className="bg-white p-3 rounded mb-3 border">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(justification.status)} flex items-center`}>
                              {getStatusIcon(justification.status)}
                              <span className="ml-1">{justification.status}</span>
                            </span>
                          </div>
                          {justification.status === "Pending" && (
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleApproveJustification(justification.id)}
                                className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => handleRejectJustification(justification.id)}
                                className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600"
                              >
                                Reject
                              </button>
                            </div>
                          )}
                        </div>
                        <p className="text-gray-700 text-sm mb-2">{justification.justificationText}</p>
                        <p className="text-xs text-gray-500">
                          Submitted by: {justification.email} | 
                          Date: {new Date(justification.createdAt).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  
                  {justifications.filter(j => j.warningId === warning.id).length === 0 && (
                    <p className="text-gray-500 text-sm italic">No justifications submitted yet</p>
                  )}
                </div>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  })}
</tbody>

        </table>
      </div>

      {/* Warning Modal */}
      <AddWarningModal
        isModalOpen={isModalOpen}
        isEdit={isEdit}
        onSubmit={onSubmit}
        handleSubmit={handleSubmit}
        setIsModalOpen={setIsModalOpen}
        register={register}
        reset={reset}
        employees={employees}
      />

      <Toaster />
    </div>
  );
};

export default WarningDetails;
